import type { PickerItemValue, PickerSectionData } from "./Picker.types";

/** Each value of an `exclusive` section to the other values of that section; `undefined` when no section is exclusive. */
export const collectExclusiveSiblings = <T extends PickerItemValue>(
    sections: ReadonlyArray<PickerSectionData<T>> | undefined,
): ReadonlyMap<T, readonly T[]> | undefined => {
    const exclusiveSections =
        sections?.filter((section) => section.exclusive === true) ?? [];
    if (exclusiveSections.length === 0) {
        return undefined;
    }
    const siblings = new Map<T, readonly T[]>();
    for (const section of exclusiveSections) {
        const values = section.items.map((item) => item.value);
        for (const value of values) {
            siblings.set(
                value,
                values.filter((other) => other !== value),
            );
        }
    }
    return siblings;
};

/** Keeps one value per exclusive section: a newly added value drops the others of its section. */
export const keepOnePerExclusiveSection = <T extends PickerItemValue>({
    previousValues,
    nextValues,
    siblings,
}: {
    previousValues: readonly T[];
    nextValues: T[];
    siblings: ReadonlyMap<T, readonly T[]>;
}): T[] => {
    const previous = new Set(previousValues);
    const dropped = new Set<T>(
        nextValues
            .filter((value) => !previous.has(value))
            .flatMap((value) => siblings.get(value) ?? []),
    );
    return dropped.size === 0
        ? nextValues
        : nextValues.filter((value) => !dropped.has(value));
};
